import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ExternalLink, Filter, Loader2 } from "lucide-react";
import { fetchRules } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { StatusBadge } from "@/components/StatusBadge";
import { PROVINCE_COLORS, PROVINCES } from "@/lib/constants";

const PROGRAM_STATUS = {
  active: { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  paused: { label: "Paused", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function StreamMatrix() {
  const { result } = useProfile();
  const [rules, setRules] = useState(null);
  const [q, setQ] = useState("");
  const [prov, setProv] = useState("all");

  useEffect(() => {
    fetchRules().then(setRules).catch(() => setRules({ streams: [] }));
  }, []);

  const userMap = useMemo(() => {
    const m = {};
    (result?.streams || []).forEach((s) => (m[s.id] = s));
    return m;
  }, [result]);

  if (!rules) {
    return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>;
  }

  const filtered = rules.streams.filter((s) => {
    const matchesQ = (s.name + s.province_name + s.stream_group).toLowerCase().includes(q.toLowerCase());
    const matchesP = prov === "all" || s.province === prov;
    return matchesQ && matchesP;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Stream matrix</h1>
      <p className="mt-2 max-w-2xl text-slate-600">
        Compare every supported PNP stream side by side. {result ? "Your standing is shown from your latest assessment." : "Run an assessment to see your standing on each stream."}
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="matrix-search"
            placeholder="Search streams or provinces…"
            className="w-full rounded-md border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm shadow-sm outline-none focus:border-[#C8102E] focus:ring-2 focus:ring-[#C8102E]/20" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <select value={prov} onChange={(e) => setProv(e.target.value)} data-testid="matrix-province-filter"
            className="rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm outline-none focus:border-[#C8102E]">
            <option value="all">All provinces</option>
            {PROVINCES.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Mobile: stacked cards */}
      <div className="mt-6 space-y-3 md:hidden" data-testid="matrix-cards">
        {filtered.map((s) => {
          const u = userMap[s.id];
          const ps = PROGRAM_STATUS[s.status] || PROGRAM_STATUS.closed;
          return (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`matrix-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: PROVINCE_COLORS[s.province] }}>{s.province}</span>
                    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ps.cls}`}>{ps.label}</span>
                  </div>
                  <div className="mt-2 font-semibold leading-snug text-slate-900">{s.name}</div>
                  <div className="text-xs text-slate-500">{s.stream_group}</div>
                </div>
                <a href={s.official_url} target="_blank" rel="noreferrer" aria-label="Official page" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-50 hover:text-slate-900">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><dt className="text-xs uppercase tracking-wider text-slate-400">Scoring</dt><dd className="text-slate-700">{s.scoring_model === "crs_based" ? "CRS" : s.scoring_model === "grid" ? "Points grid" : "Eligibility"}</dd></div>
                <div><dt className="text-xs uppercase tracking-wider text-slate-400">Recent cutoff</dt><dd className="font-mono-tech text-slate-700">{s.cutoff != null ? `~${s.cutoff}` : "—"}</dd></div>
                <div className="col-span-2"><dt className="text-xs uppercase tracking-wider text-slate-400">Your standing</dt>
                  <dd className="mt-1">{u ? <div className="flex flex-wrap items-center gap-2"><StatusBadge status={u.eligibility_status} size="sm" />{u.score != null && <span className="font-mono-tech text-xs text-slate-500">{u.score}</span>}</div> : <span className="text-xs text-slate-400">Run an assessment to see</span>}</dd></div>
              </dl>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No streams match your filters.</p>}
      </div>

      {/* Desktop: table */}
      <div className="mt-6 hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm md:block">
        <table className="w-full text-left text-sm" data-testid="matrix-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 font-semibold">Province</th>
              <th className="px-4 py-3 font-semibold">Stream</th>
              <th className="px-4 py-3 font-semibold">Scoring</th>
              <th className="px-4 py-3 font-semibold">Cutoff</th>
              <th className="px-4 py-3 font-semibold">Program</th>
              <th className="px-4 py-3 font-semibold">Your standing</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((s) => {
              const u = userMap[s.id];
              const ps = PROGRAM_STATUS[s.status] || PROGRAM_STATUS.closed;
              return (
                <tr key={s.id} className="hover:bg-slate-50/60" data-testid={`matrix-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white"
                      style={{ backgroundColor: PROVINCE_COLORS[s.province] }}>{s.province}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-500">{s.stream_group}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {s.scoring_model === "crs_based" ? "CRS" : s.scoring_model === "grid" ? "Points grid" : "Eligibility"}
                  </td>
                  <td className="px-4 py-3 font-mono-tech text-slate-700">{s.cutoff != null ? `~${s.cutoff}` : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ps.cls}`}>{ps.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    {u ? (
                      <div className="flex items-center gap-2">
                        <StatusBadge status={u.eligibility_status} size="sm" />
                        {u.score != null && <span className="font-mono-tech text-xs text-slate-500">{u.score}</span>}
                      </div>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a href={s.official_url} target="_blank" rel="noreferrer" aria-label="Official page" className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-400 hover:bg-slate-50 hover:text-slate-900">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No streams match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {!result && (
        <div className="mt-8 flex justify-center">
          <Link to="/assessment" className="inline-flex items-center gap-2 rounded-md bg-[#C8102E] px-6 py-3 text-sm font-semibold text-white hover:bg-[#A60C24]">
            Run an assessment to populate your standing
          </Link>
        </div>
      )}
    </div>
  );
}
